<script lang="ts" setup>
import ConfirmDialog, { type ConfirmDialogProps } from 'primevue/confirmdialog';
import BaseIcon from './BaseIcon.vue';
import BaseMedia from './BaseMedia.vue';
import VideoPlayer from './VideoPlayer.vue';
import { onClickOutside } from '@vueuse/core';
import { useConfirm } from 'primevue';
import { ref, useTemplateRef } from 'vue';
import { useMediaStore } from '@/stores/useMediaStore';
import { storeToRefs } from 'pinia';
import dayjs from '@/utils/dayjs';
import getMediaType, { type MediaType } from '@/utils/getMediaType';
import getFileIcon from '@/utils/getFileIcon';

const props = defineProps</* @vue-ignore */ ConfirmDialogProps>();

const mediaStore = useMediaStore();
const {
  type,
  src,
  name,
  downloadUrl,
  fileSize,
  icon,
  messageContent,
  messageAttachments,
  messageCreatedAt,
} = storeToRefs(mediaStore);

const elDialog = useTemplateRef('elDialog');
const { close } = useConfirm();
onClickOutside(elDialog, close);

const fullscreenSrc = ref<string>();
const fullscreenType = ref<MediaType>();

function openFullscreen(mediaSrc: string, mediaType: MediaType) {
  fullscreenSrc.value = mediaSrc;
  fullscreenType.value = mediaType;
}

function closeFullscreen() {
  fullscreenSrc.value = undefined;
  fullscreenType.value = undefined;
}

function onClose(rejectCallback: () => void) {
  messageAttachments.value = undefined;
  messageContent.value = undefined;
  messageCreatedAt.value = undefined;
  closeFullscreen();
  rejectCallback();
}
</script>

<template>
  <ConfirmDialog v-bind="props" group="media-dialog">
    <template #container="{ message, rejectCallback }">
      <div
        ref="elDialog"
        class="bg-white rounded-lg shadow-xl px-6 py-8 flex flex-col gap-5 w-100 md:w-140 lg:w-180 xl:w-220 max-w-[90vw]"
      >
        <div class="flex justify-between w-full">
          <p class="font-semibold text-sand-800 text-base mb-1">{{ message.header }}</p>
          <BaseIcon
            name="close"
            size="sm"
            class="text-sand-500 cursor-pointer max-md:hidden"
            @click="onClose(rejectCallback)"
          />
        </div>

        <!-- Message preview mode -->
        <template v-if="messageAttachments !== undefined">
          <p v-if="messageContent" class="text-sand-700 text-sm whitespace-pre-wrap">
            {{ messageContent }}
          </p>
          <div v-if="messageAttachments.length" class="flex flex-wrap gap-2">
            <div
              v-for="att in messageAttachments"
              :key="att.id"
              class="cursor-pointer"
              @click="
                openFullscreen(
                  att.content_type.startsWith('video/')
                    ? (att.mpd_url ?? att.file ?? '')
                    : (att.file ?? ''),
                  getMediaType(att.content_type),
                )
              "
            >
              <BaseMedia
                :type="getMediaType(att.content_type)"
                :src="
                  att.content_type.startsWith('video/')
                    ? (att.mpd_url ?? att.file ?? '')
                    : (att.file ?? '')
                "
                :preview="att.thumbnail_url ?? undefined"
                :name="att.original_filename ?? undefined"
                :download-url="att.file ?? undefined"
                :file-size="att.size ?? undefined"
                :icon="getFileIcon(att.content_type)"
                width="thumbnail"
                unclickable
              />
            </div>
          </div>
          <p v-if="messageCreatedAt" class="text-xs text-sand-400">
            {{ dayjs(messageCreatedAt).fromNow() }}
          </p>
        </template>

        <!-- Single-media mode -->
        <div v-else-if="type && src">
          <BaseMedia :type :src :name :download-url :file-size :icon width="full" unclickable />
        </div>
      </div>
    </template>
  </ConfirmDialog>

  <Teleport to="body">
    <Transition name="fullscreen">
      <div
        v-if="fullscreenSrc && fullscreenType"
        class="fixed inset-0 z-9999 bg-black/90 flex items-center justify-center"
        @click.self="closeFullscreen"
      >
        <BaseIcon
          name="close"
          size="lg"
          class="absolute top-4 right-4 text-white/80 cursor-pointer hover:text-white z-10"
          @click="closeFullscreen"
        />
        <img
          v-if="fullscreenType === 'image'"
          :src="fullscreenSrc"
          class="max-w-[90vw] max-h-[90vh] object-contain"
        />
        <VideoPlayer
          v-else-if="fullscreenType === 'video'"
          :url="fullscreenSrc"
          class="max-w-[90vw] max-h-[90vh]"
        />
        <audio v-else-if="fullscreenType === 'audio'" :src="fullscreenSrc" controls class="w-96" />
        <iframe
          v-else-if="fullscreenType === 'document'"
          :src="fullscreenSrc"
          class="w-[90vw] h-[90vh]"
        />
      </div>
    </Transition>
  </Teleport>
</template>

<style>
.p-dialog {
  border-radius: 12px !important;
  overflow: hidden;
  border: none !important;
}

.fullscreen-enter-active,
.fullscreen-leave-active {
  transition: opacity 0.2s ease;
}
.fullscreen-enter-from,
.fullscreen-leave-to {
  opacity: 0;
}
</style>
